import apiClient from './client';
import type { InstallationTimeWindow } from '../types/installations';
import type { PaginatedResponse } from '../types';

// ==================== Types ====================

export type TicketStatus = 'Open' | 'InProgress' | 'Pending' | 'Resolved' | 'Closed';
export type TicketPriority = 'Low' | 'Medium' | 'High' | 'Urgent';
export type TicketCategory =
  | 'Service'
  | 'Billing'
  | 'General'
  | 'Complaint'
  | 'FeatureRequest'
  | 'TroubleCall'
  | 'Maintenance'
  | 'EquipmentSwap'
  | 'NetworkIssue';

export interface CloseTicketRequest {
  latitude?: number;
  longitude?: number;
}
export type TicketQueue = 'Technical' | 'Billing' | 'General';

export interface Ticket {
  id: string;
  ticketNumber: string;
  title: string;
  category: TicketCategory;
  status: TicketStatus;
  priority: TicketPriority;
  queue: TicketQueue;
  customerAccountId?: string;
  leadId?: string;
  leadName?: string;
  customerName: string;
  customerEmail?: string;
  assignedToUserId?: string;
  assignedToUserName?: string;
  departmentId?: string;
  departmentName?: string;
  relatedInstallationId?: string;
  relatedInstallationAddress?: string;
  createdAt: string;
  resolvedAt?: string;
  closedAt?: string;
  // Location data
  latitude?: number;
  longitude?: number;
  locationAddress?: string;
  locationSource?: TicketLocationSource;
  // Field visit
  requiresFieldVisit: boolean;
  scheduledServiceDate?: string;
  scheduledTimeWindow?: InstallationTimeWindow;
  /** "08:00:00" when the tenant books hourly slots */
  scheduledStartTime?: string;
  scheduledEndTime?: string;
  estimatedServiceDuration?: number;
  /** "Tuesday, October 7, 2026, Morning (8:00 AM - 12:00 PM)" */
  scheduledDisplay?: string;
  assignedFieldWorkerId?: string;
  assignedFieldWorkerName?: string;
}

/** The newest scheduling link sent to the customer for a ticket's visit. */
export interface TicketSchedulingLink {
  id: string;
  channel: 'email' | 'sms' | 'both';
  sentTo?: string;
  sentAt: string;
  expiresAt: string;
  usedAt?: string;
  revokedAt?: string;
  isActive: boolean;
  url: string;
}

export interface TicketDetail extends Ticket {
  schedulingLink?: TicketSchedulingLink;
  description?: string;
  resolutionNotes?: string;
  customerPhone?: string;
  relatedEquipmentItemId?: string;
  relatedEquipmentName?: string;
  relatedEquipmentSerial?: string;
  createdByUserId: string;
  createdByUserName?: string;
  updatedAt: string;
  /**
   * Customer/Lead service address snapshotted at ticket creation. Distinct
   * from `locationAddress` (the work-site address, which may be elsewhere).
   */
  serviceAddress?: string;
  notes: TicketNote[];
  statusHistory: TicketStatusHistory[];
}

export interface TicketNote {
  id: string;
  ticketId: string;
  content: string;
  isInternal: boolean;
  createdByUserId: string;
  createdByUserName?: string;
  createdAt: string;
}

export interface TicketStatusHistory {
  id: string;
  oldStatus?: TicketStatus;
  newStatus: TicketStatus;
  changedByUserId: string;
  changedByUserName?: string;
  notes?: string;
  createdAt: string;
}

export interface TicketStats {
  totalOpen: number;
  totalInProgress: number;
  totalPending: number;
  totalResolved: number;
  totalClosed: number;
  technicalQueueCount: number;
  billingQueueCount: number;
  generalQueueCount: number;
  unassignedCount: number;
  urgentCount: number;
  highCount: number;
}

export interface MyTicketStats {
  assignedToMe: number;
  openByMe: number;
  resolvedByMeThisWeek: number;
  closedByMeThisWeek: number;
}

export interface TicketFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: TicketStatus;
  priority?: TicketPriority;
  category?: TicketCategory;
  queue?: TicketQueue;
  assignedToUserId?: string;
  departmentId?: string;
  customerAccountId?: string;
  unassigned?: boolean;
  fromDate?: string;
  toDate?: string;
}

export type TicketLocationSource = 'GPS' | 'Manual' | 'Infrastructure' | 'CustomerAddress' | 'Unknown';

export interface CreateTicketRequest {
  customerAccountId?: string;
  leadId?: string;
  title: string;
  description?: string;
  supportTicketTypeId?: string;
  category?: TicketCategory;
  priority?: TicketPriority;
  relatedInstallationId?: string;
  relatedEquipmentItemId?: string;
  departmentId?: string;
  // Location data (for field-created tickets)
  latitude?: number;
  longitude?: number;
  locationAddress?: string;
  locationSource?: TicketLocationSource;
  // Related fiber infrastructure
  relatedFiberEnclosureId?: string;
  relatedFiberRouteId?: string;
  /** Null takes the ticket type's default */
  requiresFieldVisit?: boolean;
  /** Book the visit in the same call (the office just agreed a time on the phone) */
  schedule?: ScheduleTicketRequest;
}

export interface ScheduleTicketRequest {
  /** YYYY-MM-DD */
  date: string;
  timeWindow?: InstallationTimeWindow;
  /** "08:00:00", hourly-slot tenants only */
  startTime?: string;
  endTime?: string;
  estimatedDurationMinutes?: number;
  /** Text/email the customer a confirmation. Default true. */
  notifyCustomer?: boolean;
}

export interface UpdateTicketRequest {
  title?: string;
  description?: string;
  category?: TicketCategory;
  priority?: TicketPriority;
  relatedInstallationId?: string;
  relatedEquipmentItemId?: string;
}

export interface AddNoteRequest {
  content: string;
  isInternal?: boolean;
}

export interface AssignTicketRequest {
  assignToUserId: string;
}

export type TicketAttachmentCategory = 'General' | 'Photo' | 'Document' | 'Invoice' | 'Diagram';

export interface TicketAttachment {
  id: string;
  ticketId: string;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  fileSizeDisplay: string;
  category: TicketAttachmentCategory;
  description?: string;
  uploadedByUserName?: string;
  createdAt: string;
  downloadUrl?: string;
}

export interface ResolveTicketRequest {
  resolutionNotes?: string;
}

export interface BulkTicketRequest {
  ticketIds: string[];
}

export interface BulkAssignRequest extends BulkTicketRequest {
  assignToUserId: string;
}

export interface BulkUpdatePriorityRequest extends BulkTicketRequest {
  priority: TicketPriority;
}

// ==================== API Functions ====================

export async function getTickets(filters?: TicketFilters): Promise<PaginatedResponse<Ticket>> {
  const response = await apiClient.get<PaginatedResponse<Ticket>>('/tickets', { params: filters });
  return response.data;
}

export async function getTicket(id: string): Promise<TicketDetail> {
  const response = await apiClient.get<TicketDetail>(`/tickets/${id}`);
  return response.data;
}

export async function getTicketStats(): Promise<TicketStats> {
  const response = await apiClient.get<TicketStats>('/tickets/stats');
  return response.data;
}

export async function getMyTicketStats(): Promise<MyTicketStats> {
  const response = await apiClient.get<MyTicketStats>('/tickets/my-stats');
  return response.data;
}

export async function createTicket(data: CreateTicketRequest): Promise<TicketDetail> {
  const response = await apiClient.post<TicketDetail>('/tickets', data);
  return response.data;
}

export async function updateTicket(id: string, data: UpdateTicketRequest): Promise<TicketDetail> {
  const response = await apiClient.put<TicketDetail>(`/tickets/${id}`, data);
  return response.data;
}

export async function assignTicket(id: string, assignToUserId: string): Promise<TicketDetail> {
  const response = await apiClient.post<TicketDetail>(`/tickets/${id}/assign`, { assignToUserId });
  return response.data;
}

export async function unassignTicket(id: string): Promise<TicketDetail> {
  const response = await apiClient.post<TicketDetail>(`/tickets/${id}/unassign`);
  return response.data;
}

export async function assignDepartment(id: string, departmentId: string | null): Promise<TicketDetail> {
  const response = await apiClient.post<TicketDetail>(`/tickets/${id}/assign-department`, { departmentId });
  return response.data;
}

export async function resolveTicket(id: string, data?: ResolveTicketRequest): Promise<TicketDetail> {
  const response = await apiClient.post<TicketDetail>(`/tickets/${id}/resolve`, data);
  return response.data;
}

export async function closeTicket(id: string, request?: CloseTicketRequest): Promise<TicketDetail> {
  const response = await apiClient.post<TicketDetail>(`/tickets/${id}/close`, request || {});
  return response.data;
}

/** Book or move the ticket's field visit. Fails with the reason when the day is blocked or full. */
export async function scheduleTicketVisit(id: string, data: ScheduleTicketRequest): Promise<TicketDetail> {
  const response = await apiClient.put<TicketDetail>(`/tickets/${id}/schedule`, data);
  return response.data;
}

export async function clearTicketVisit(id: string): Promise<TicketDetail> {
  const response = await apiClient.delete<TicketDetail>(`/tickets/${id}/schedule`);
  return response.data;
}

/** Text and/or email the customer a link to pick the visit time themselves. */
export async function sendSchedulingLink(id: string, channel: 'email' | 'sms' | 'both'): Promise<TicketSchedulingLink> {
  const response = await apiClient.post<TicketSchedulingLink>(`/tickets/${id}/scheduling-link`, { channel });
  return response.data;
}

export async function reopenTicket(id: string): Promise<TicketDetail> {
  const response = await apiClient.post<TicketDetail>(`/tickets/${id}/reopen`);
  return response.data;
}

export async function addTicketNote(id: string, data: AddNoteRequest): Promise<TicketNote> {
  const response = await apiClient.post<TicketNote>(`/tickets/${id}/notes`, data);
  return response.data;
}

export async function deleteTicket(id: string): Promise<void> {
  await apiClient.delete(`/tickets/${id}`);
}

// Bulk operations
export async function bulkAssignTickets(data: BulkAssignRequest): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>('/tickets/bulk/assign', data);
  return response.data;
}

export async function bulkCloseTickets(data: BulkTicketRequest): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>('/tickets/bulk/close', data);
  return response.data;
}

export async function bulkUpdatePriority(data: BulkUpdatePriorityRequest): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>('/tickets/bulk/update-priority', data);
  return response.data;
}

// ==================== Helper Functions ====================

export function getStatusLabel(status: TicketStatus): string {
  const labels: Record<TicketStatus, string> = {
    Open: 'Open',
    InProgress: 'In Progress',
    Pending: 'Pending',
    Resolved: 'Resolved',
    Closed: 'Closed',
  };
  return labels[status] || status;
}

export function getStatusVariant(status: TicketStatus): 'default' | 'warning' | 'success' | 'error' {
  const variants: Record<TicketStatus, 'default' | 'warning' | 'success' | 'error'> = {
    Open: 'default',
    InProgress: 'warning',
    Pending: 'warning',
    Resolved: 'success',
    Closed: 'default',
  };
  return variants[status] || 'default';
}

export function getStatusColor(status: TicketStatus): string {
  const colors: Record<TicketStatus, string> = {
    Open: 'bg-blue-100 text-blue-800',
    InProgress: 'bg-yellow-100 text-yellow-800',
    Pending: 'bg-orange-100 text-orange-800',
    Resolved: 'bg-green-100 text-green-800',
    Closed: 'bg-gray-100 text-gray-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

export function getPriorityLabel(priority: TicketPriority): string {
  const labels: Record<TicketPriority, string> = {
    Low: 'Low',
    Medium: 'Medium',
    High: 'High',
    Urgent: 'Urgent',
  };
  return labels[priority] || priority;
}

export function getPriorityColor(priority: TicketPriority): string {
  const colors: Record<TicketPriority, string> = {
    Low: 'bg-gray-100 text-gray-800',
    Medium: 'bg-blue-100 text-blue-800',
    High: 'bg-orange-100 text-orange-800',
    Urgent: 'bg-red-100 text-red-800',
  };
  return colors[priority] || 'bg-gray-100 text-gray-800';
}

export function getCategoryLabel(category: TicketCategory): string {
  const labels: Record<TicketCategory, string> = {
    Service: 'Service Issue',
    Billing: 'Billing',
    General: 'General Inquiry',
    Complaint: 'Complaint',
    FeatureRequest: 'Feature Request',
    TroubleCall: 'Trouble Call',
    Maintenance: 'Maintenance',
    EquipmentSwap: 'Equipment Swap',
    NetworkIssue: 'Network Issue',
  };
  return labels[category] || category;
}

export function getCategoryColor(category: TicketCategory): string {
  const colors: Record<TicketCategory, string> = {
    Service: 'bg-purple-100 text-purple-800',
    Billing: 'bg-green-100 text-green-800',
    General: 'bg-blue-100 text-blue-800',
    Complaint: 'bg-red-100 text-red-800',
    FeatureRequest: 'bg-indigo-100 text-indigo-800',
    TroubleCall: 'bg-orange-100 text-orange-800',
    Maintenance: 'bg-yellow-100 text-yellow-800',
    EquipmentSwap: 'bg-cyan-100 text-cyan-800',
    NetworkIssue: 'bg-pink-100 text-pink-800',
  };
  return colors[category] || 'bg-gray-100 text-gray-800';
}

export function getQueueLabel(queue: TicketQueue): string {
  const labels: Record<TicketQueue, string> = {
    Technical: 'Technical Support',
    Billing: 'Billing',
    General: 'General',
  };
  return labels[queue] || queue;
}

export function getQueueColor(queue: TicketQueue): string {
  const colors: Record<TicketQueue, string> = {
    Technical: 'bg-purple-100 text-purple-800',
    Billing: 'bg-green-100 text-green-800',
    General: 'bg-blue-100 text-blue-800',
  };
  return colors[queue] || 'bg-gray-100 text-gray-800';
}

// Priority sorting (higher priority = lower sort value)
export function getPrioritySortValue(priority: TicketPriority): number {
  const values: Record<TicketPriority, number> = {
    Urgent: 0,
    High: 1,
    Medium: 2,
    Low: 3,
  };
  return values[priority] ?? 4;
}

// Check if ticket is actionable (not closed/resolved)
export function isTicketActionable(status: TicketStatus): boolean {
  return status !== 'Closed' && status !== 'Resolved';
}

// Format ticket age
export function getTicketAge(createdAt: string): string {
  const created = new Date(createdAt);
  const now = new Date();
  const diffMs = now.getTime() - created.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `${diffDays}d`;
  }
  if (diffHours > 0) {
    return `${diffHours}h`;
  }
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  return `${diffMinutes}m`;
}

// ========== Attachment API Functions ==========

/**
 * Get all attachments for a ticket
 */
export async function getTicketAttachments(ticketId: string): Promise<TicketAttachment[]> {
  const response = await apiClient.get(`/tickets/${ticketId}/attachments`);
  return response.data;
}

/**
 * Upload an attachment to a ticket
 */
export async function uploadTicketAttachment(
  ticketId: string,
  file: File,
  category: TicketAttachmentCategory = 'General',
  description?: string
): Promise<TicketAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', category);
  if (description) {
    formData.append('description', description);
  }

  const response = await apiClient.post(`/tickets/${ticketId}/attachments`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

/**
 * Delete a ticket attachment
 */
export async function deleteTicketAttachment(ticketId: string, attachmentId: string): Promise<void> {
  await apiClient.delete(`/tickets/${ticketId}/attachments/${attachmentId}`);
}
